import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ActivityGqlOperation, ApiBaseState, FilterRequestProps, GqlApiResponse, ReviewRequestProps, SortRequestProps } from 'store/api/api.types';
import { updateApiBaseState } from 'store/api/apiUtils';
import queryActivitiesGqlApi, { resetStateWithDefaultApiBaseState } from 'store/api/activitiesGqlApi';
import { DefaultApiBaseState, DefaultRequestProps } from 'store/api/ApiConstants';
import { ReviewResponse } from 'gql/autogen/ReviewResponse';
import { measureReviewApiResponse } from 'store/api/measurements/reviewGqlMeasurement';
import { Filters } from 'gql/autogen/Filters';
import { ReviewSortState } from 'component/page/detail/Review/ReviewSortAndFilter/ReviewSortAndFilter.types';
import { SortCode } from 'gql/autogen/SortCode';
import { SortOrder } from 'gql/autogen/SortOrder';

export interface ReviewState extends ApiBaseState {
    reviewResponse?: ReviewResponse;
    currentFilter: Filters;
    currentSort: ReviewSortState;
    isHideReview: boolean;
}

export const reviewInitialState: ReviewState = {
    ...DefaultApiBaseState,
    currentFilter: { valueFilters: [], rangeFilters: [] },
    currentSort: { code: SortCode.Default, order: SortOrder.Default, type: 'Default' },
    isHideReview: false,
};

let reviewInitialStateLazy: ReviewState;

const reviewSlice = createSlice({
    name: 'review',
    initialState: (): ReviewState => {
        // Intentional: Somehow webpack is unable to load variable DefaultApiBaseState in this file. This implementation is a lazy initialization implementation.
        if (!reviewInitialStateLazy) {
            reviewInitialStateLazy = { ...reviewInitialState, ...DefaultApiBaseState };
        }
        return reviewInitialStateLazy;
    },
    reducers: {
        resetReviewState: (state) => {
            resetStateWithDefaultApiBaseState(state);
            state.reviewResponse = undefined;
        },
        reviewLoadFinished: (state, action: PayloadAction<GqlApiResponse<ReviewResponse, ReviewRequestProps>>) => {
            updateApiBaseState(state, action);
            const prevMatrix = state.reviewResponse?.result?.matrix;
            state.reviewResponse = action.payload.result;
            if (!state.reviewResponse.result && prevMatrix) {
                state.reviewResponse.result = { matrix: prevMatrix };
            }

            state.isHideReview = state.isSuccess && !state.isLoading && state.currentFilter.valueFilters.length === 0 && state.reviewResponse?.result?.reviews?.length === 0;
            if (state.isError || state.isSuccess) {
                measureReviewApiResponse(state.isSuccess, action.payload.pageTypeId, action.payload.config, state.reviewResponse?.result, state.error);
            }
        },
        setCurrentReviewFilter: (state, action: PayloadAction<Filters>) => {
            state.currentFilter = action.payload;
        },
        setCurrentReviewSort: (state, action: PayloadAction<ReviewSortState>) => {
            state.currentSort = action.payload;
        },
        resetReviewSortFilter: (state) => {
            state.currentFilter = reviewInitialState.currentFilter;
            state.currentSort = reviewInitialState.currentSort;
        },
    },
});

export const fetchReview = (activityId: number, sortProps?: SortRequestProps, filterProps?: FilterRequestProps, pageNumber?: number, pageSize?: number) => {
    const reviewDefaultProps = DefaultRequestProps.review;
    const args: { props: ReviewRequestProps } = {
        props: {
            activityId,
            pagination: {
                number: pageNumber || reviewDefaultProps.pagination.number,
                size: pageSize || reviewDefaultProps.pagination.size,
            },
            sort: sortProps?.sort,
            filters: filterProps?.filters,
        },
    };

    queryActivitiesGqlApi({ operation: ActivityGqlOperation.review, args });
};

export const { resetReviewState, reviewLoadFinished, setCurrentReviewFilter, setCurrentReviewSort, resetReviewSortFilter } = reviewSlice.actions;
export default reviewSlice.reducer;
